// client/src/components/secure/FormsPanel.js — the forms for one client.
//
// Three columns of the same question: what must be done now, what cannot be
// done yet and why, and what is already signed. The "why" is the point — a
// blocked form says which document has to be signed first, because that order
// is the compliance, not a preference.
//
// One click generates a form filled from the record. One click generates a
// whole packet. Signing is either a link the client uses, or print-sign-scan,
// because most of this office still works on paper and a system that only
// understands e-signatures quietly forces a practice change nobody agreed to.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import Modal from './Modal';
import { ProtectedLink } from '../../utils/openProtected';

const STAGE_LABEL = {
    customer: 'Customer', engagement: 'Engagement', listing: 'Listing', marketing: 'Marketing',
    offer: 'Offer', under_contract: 'Under contract', contingency: 'Contingencies',
    closing: 'Closing', post_closing: 'File closeout',
};
const STATUS_TONE = {
    draft: 'bg-gray-100 text-gray-700',
    awaiting_signature: 'bg-amber-100 text-amber-800',
    partially_signed: 'bg-blue-100 text-blue-800',
    signed: 'bg-green-100 text-green-800',
    void: 'bg-red-50 text-red-600',
    superseded: 'bg-gray-100 text-gray-500',
};

/**
 * The same panel serves a client, a licensee and an employee.
 *
 * Deliberately one component: the signature evidence is identical whoever signs, so
 * a second panel would be a second place for the signing UI to drift. What differs
 * is the subject, and that is a prop.
 */
const FormsPanel = ({ clientId, clientName, transactionId = null,
                      subjectType = 'client', subjectId = null, subjectName = null }) => {
    const subject = subjectType === 'client' ? 'client' : subjectType;
    const sid = subjectType === 'client' ? clientId : subjectId;
    const who = subjectName || clientName;
    const api = useApiClient();
    const [state, setState] = useState({ due: [], blocked: [], done: [], upcoming: [], context: {} });
    const [catalog, setCatalog] = useState({ forms: [], packets: [] });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState(null);
    const [dualAgency, setDualAgency] = useState(false);
    const [detail, setDetail] = useState(null);
    const [links, setLinks] = useState(null);

    const load = useCallback(async () => {
        if (!sid) return;
        setLoading(true);
        try {
            const qs = new URLSearchParams({ subject_type: subject, subject_id: sid });
            if (transactionId) qs.set('transaction_id', transactionId);
            if (dualAgency) qs.set('dual_agency', '1');
            const [next, cat] = await Promise.all([
                api.get(`/api/forms-engine/next?${qs}`),
                api.get('/api/forms-engine/catalog'),
            ]);
            setState(next);
            setCatalog(cat);
            setError('');
        } catch (err) {
            setError(err.message || 'Could not load the forms.');
        } finally {
            setLoading(false);
        }
    }, [api, subject, sid, transactionId, dualAgency]);

    useEffect(() => { load(); }, [load]);

    const generate = async (body, label) => {
        setBusy(label);
        try {
            const res = await api.post('/api/forms-engine/generate', {
                subject_type: subject, subject_id: sid,
                transaction_id: transactionId, ...body });
            const made = res.created.length;
            const skipped = res.skipped.length;
            setNotice(`${made} document${made === 1 ? '' : 's'} prepared`
                + (skipped ? `, ${skipped} skipped (already on file)` : '') + '.');
            await load();
        } catch (err) {
            setError(err.message || 'Could not generate.');
        } finally {
            setBusy(null);
        }
    };

    const open = async (instanceId) => {
        try {
            setDetail(await api.get(`/api/forms-engine/instances/${instanceId}`));
        } catch (err) {
            setError(err.message);
        }
    };

    // Some disclosures are discharged by being GIVEN, not signed — 454/15-15 is the
    // one that matters here. Asking a represented buyer to sign a no-agency
    // disclosure puts two contradictory documents in the file, so for those forms
    // the primary action is delivery and there is no signature to chase.
    const deliver = async (instanceId, how = 'email') => {
        setBusy(`deliver-${instanceId}`);
        setError(''); setNotice(null);
        try {
            const res = await api.post(`/api/forms-engine/instances/${instanceId}/deliver`, { how });
            setNotice(res.message);
            await load();
            setDetail(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(null);
        }
    };

    const send = async (instanceId) => {
        setBusy(`send-${instanceId}`);
        try {
            const res = await api.post(`/api/forms-engine/instances/${instanceId}/send`, {});
            // Keep the whole response: the useful question after sending is not
            // "what is the URL" but "did it reach them, and if not, why not".
            setLinks(res);
            await load();
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(null);
        }
    };

    const signInternal = async (instanceId) => {
        try {
            // No role: the server takes the firm's line from the form (managing broker where
            // the form has one, else the licensee). Sending 'agent' failed with 400 on every
            // form whose firm line is the managing broker's, e.g. the listing agreement.
            await api.post(`/api/forms-engine/instances/${instanceId}/sign-internal`, {});
            setDetail(null);
            await load();
        } catch (err) { setError(err.message); }
    };

    const uploadScan = async (instanceId, file) => {
        const fd = new FormData();
        fd.append('file', file);
        try {
            await api.post(`/api/forms-engine/instances/${instanceId}/scan`, fd, true);
            setNotice('Signed copy filed.');
            setDetail(null);
            await load();
        } catch (err) { setError(err.message); }
    };

    const voidForm = async (instanceId) => {
        const reason = window.prompt('Why is this being withdrawn? (kept in the file)');
        if (reason === null) return;
        try {
            await api.post(`/api/forms-engine/instances/${instanceId}/void`, { reason });
            setDetail(null);
            await load();
        } catch (err) { setError(err.message); }
    };

    const byStage = useMemo(() => {
        const groups = {};
        for (const f of state.due) (groups[f.stage] ||= []).push(f);
        return groups;
    }, [state.due]);

    if (!sid) return null;

    return (
        <div className="bg-white border rounded-lg shadow-sm">
            <div className="px-5 py-4 border-b flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                        {subject === 'client' ? 'Transaction forms'
                            : subject === 'agent' ? 'Sponsorship and policy documents'
                            : 'Employment and policy documents'}
                    </h3>
                    <p className="text-sm text-gray-500">
                        {who ? `${who} — ` : ''}
                        {subject === 'client'
                            ? 'in the order the statute requires.'
                            : 'signed electronically, with the same evidence as a client document.'}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {subject === 'client' && (
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                        <input type="checkbox" checked={dualAgency}
                               onChange={(e) => setDualAgency(e.target.checked)} />
                        Firm is on both sides (dual agency)
                    </label>
                    )}
                    <button onClick={load} className="text-sm text-blue-600 hover:underline">Refresh</button>
                </div>
            </div>

            {/* One click: the packets */}
            <div className="px-5 py-3 border-b bg-gray-50 flex flex-wrap gap-2">
                {catalog.packets.map((p) => (
                    <button key={p.packet_key}
                            title={p.description || ''}
                            disabled={busy === p.packet_key}
                            onClick={() => generate({ packet_key: p.packet_key }, p.packet_key)}
                            className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white hover:bg-blue-50
                                       hover:border-blue-300 disabled:opacity-50">
                        {busy === p.packet_key ? 'Preparing…' : p.title}
                    </button>
                ))}
            </div>

            {error && <div className="mx-5 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">{error}</div>}
            {notice && (
                <div className="mx-5 mt-4 p-3 bg-green-50 border border-green-200 text-green-800 text-sm rounded flex justify-between">
                    <span>{notice}</span>
                    <button onClick={() => setNotice(null)} className="text-green-700">×</button>
                </div>
            )}

            {loading ? (
                <p className="p-5 text-gray-500">Loading…</p>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 divide-y lg:divide-y-0 lg:divide-x">
                    {/* DUE */}
                    <div className="p-5">
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                            Due now ({state.due.length})
                            {state.context?.stage && (
                                <span className="ml-2 font-normal normal-case text-gray-400">
                                    · {String(state.context.stage).replace(/_/g, ' ')} stage
                                </span>
                            )}
                        </h4>
                        {state.due.length === 0 && <p className="text-sm text-gray-400">Nothing outstanding.</p>}
                        {Object.entries(byStage).map(([stage, forms]) => (
                            <div key={stage} className="mb-4">
                                <p className="text-[11px] font-semibold text-gray-400 uppercase mb-1">
                                    {STAGE_LABEL[stage] || stage}
                                </p>
                                {forms.map((f) => (
                                    <div key={f.form_key} className="mb-2 p-2.5 border rounded hover:border-blue-300">
                                        <div className="flex items-start justify-between gap-2">
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">{f.title}</p>
                                                {f.statutory_ref && (
                                                    <p className="text-[11px] text-gray-500">{f.statutory_ref}</p>
                                                )}
                                                {f.source === 'external' && (
                                                    <p className="text-[11px] text-amber-700 mt-0.5">
                                                        Licensed form — data sheet prepared, attach the executed copy.
                                                    </p>
                                                )}
                                            </div>
                                            {f.instance ? (
                                                <button onClick={() => open(f.instance.instance_id)}
                                                        className={`px-2 py-0.5 text-[11px] rounded ${STATUS_TONE[f.instance.status]}`}>
                                                    {f.instance.status.replace('_', ' ')}
                                                </button>
                                            ) : (
                                                <button disabled={busy === f.form_key}
                                                        onClick={() => generate({ form_key: f.form_key }, f.form_key)}
                                                        className="px-2.5 py-1 text-xs rounded bg-blue-600 text-white
                                                                   hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap">
                                                    {busy === f.form_key ? '…' : 'Prepare'}
                                                </button>
                                            )}
                                        </div>
                                        {f.guidance && <p className="text-[11px] text-gray-500 mt-1">{f.guidance}</p>}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>

                    {/* BLOCKED */}
                    <div className="p-5 bg-gray-50/50">
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                            Not yet ({state.blocked.length})
                        </h4>
                        {state.blocked.length === 0 && <p className="text-sm text-gray-400">Nothing waiting.</p>}
                        {state.blocked.map((f) => (
                            <div key={f.form_key} className="mb-2 p-2.5 border border-dashed rounded">
                                <p className="text-sm text-gray-700">{f.title}</p>
                                <p className="text-[11px] text-amber-700 mt-0.5">{f.reason}</p>
                            </div>
                        ))}

                        {/* Later stages. Collapsed on purpose: these are not outstanding
                            work, and listing them beside what is due was what made a new
                            client look like it needed 27 documents signed today. */}
                        {(state.upcoming || []).length > 0 && (
                            <details className="mt-3">
                                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                                    Later in the transaction ({state.upcoming.length})
                                </summary>
                                <div className="mt-2">
                                    {state.upcoming.map((f) => (
                                        <div key={f.form_key} className="mb-1.5 px-2.5 py-1.5 rounded bg-white/70">
                                            <p className="text-[13px] text-gray-500">{f.title}</p>
                                            <p className="text-[11px] text-gray-400">{f.reason}</p>
                                        </div>
                                    ))}
                                </div>
                            </details>
                        )}
                    </div>

                    {/* DONE */}
                    <div className="p-5">
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                            Signed ({state.done.length})
                        </h4>
                        {state.done.length === 0 && <p className="text-sm text-gray-400">Nothing signed yet.</p>}
                        {state.done.map((f) => (
                            <div key={f.form_key} className="mb-2 p-2.5 border rounded bg-green-50/40 flex justify-between gap-2">
                                <p className="text-sm text-gray-800">{f.title}</p>
                                {f.instance && (
                                    <button onClick={() => open(f.instance.instance_id)}
                                            className="text-xs text-blue-600 hover:underline whitespace-nowrap">Open</button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* What happened when it was sent. The link is the fallback, not the point:
                an electronic signature that needs a human to carry the URL is just a
                slower way of printing. */}
            {links && (
                <Modal open onClose={() => setLinks(null)} title="Sent for signature">
                    <p className={`text-sm mb-3 ${links.delivered > 0 ? 'text-green-700' : 'text-amber-800'}`}>
                        {links.message}
                    </p>

                    {(links.links || []).map((l) => (
                        <div key={l.role}
                             className={`mb-3 p-3 border rounded ${l.delivered
                                 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                            <p className="text-sm font-medium">
                                {l.name} <span className="text-gray-500">({l.role})</span>
                                {l.delivered
                                    ? <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-800">
                                        emailed to {l.email}
                                      </span>
                                    : <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                                        not emailed
                                      </span>}
                            </p>
                            {!l.delivered && (
                                <>
                                    <p className="text-xs text-amber-900 mt-1">
                                        {l.delivery_error || 'Could not be emailed.'} The link below still works —
                                        send it however you normally reach them.
                                    </p>
                                    <input readOnly value={l.url}
                                           onFocus={(e) => e.target.select()}
                                           className="mt-1 w-full text-xs font-mono p-2 border rounded bg-white" />
                                </>
                            )}
                        </div>
                    ))}

                    <p className="text-xs text-gray-500 mt-2">
                        Each link works once and expires in 14 days. Sending again issues a fresh link and
                        invalidates the old one, which is how to handle “I lost the email”.
                    </p>
                </Modal>
            )}

            {/* One form */}
            {detail && (
                <Modal open onClose={() => setDetail(null)} title={detail.title}>
                    <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                            <span className={`px-2 py-0.5 rounded text-xs ${STATUS_TONE[detail.status]}`}>
                                {detail.status.replace('_', ' ')}
                            </span>
                            {detail.statutory_ref && <span className="text-gray-500 text-xs">{detail.statutory_ref}</span>}
                        </div>

                        {detail.satisfied_by === 'delivery' && (
                            <p className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded p-2">
                                This disclosure is discharged by being <strong>given</strong> to the consumer,
                                not signed. A represented client should not sign it — a file holding both this
                                and a representation agreement cannot say what the relationship was.
                            </p>
                        )}

                        {detail.storage_path ? (
                            <ProtectedLink href={detail.storage_path}
                               className="inline-block text-sm text-blue-600 hover:underline">
                                Open the document ↗
                            </ProtectedLink>
                        ) : (
                            <p className="text-sm text-amber-700">
                                This is a licensed form held outside the system. Print the firm's blank, complete it
                                from the prepared data, and upload the executed copy below.
                            </p>
                        )}

                        <div>
                            <p className="text-xs font-semibold uppercase text-gray-500 mb-1">Signatures</p>
                            {detail.signatures.length === 0 && (
                                <p className="text-sm text-gray-400">Not sent for signature yet.</p>
                            )}
                            {detail.signatures.map((s) => (
                                <div key={s.signature_id} className="flex justify-between text-sm py-1 border-b last:border-0">
                                    <span>{s.signer_name} <span className="text-gray-400">({s.signer_role})</span></span>
                                    <span className={s.signed_at ? 'text-green-700' : 'text-gray-400'}>
                                        {s.signed_at
                                            ? `${s.method === 'wet' ? 'Signed on paper' : 'Signed electronically'} ${new Date(s.signed_at).toLocaleDateString()}`
                                            : 'Awaiting'}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {detail.delivered_at && (
                            <p className="text-xs text-green-700">
                                Given to the client {new Date(detail.delivered_at).toLocaleDateString()}
                                {detail.delivered_how ? ` (${detail.delivered_how.replace(/_/g, ' ')})` : ''}.
                            </p>
                        )}

                        <div className="flex flex-wrap gap-2 pt-2 border-t">
                            {detail.satisfied_by === 'delivery' ? (
                                <>
                                    <button onClick={() => deliver(detail.instance_id, 'email')}
                                            disabled={busy === `deliver-${detail.instance_id}`}
                                            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                                        {busy === `deliver-${detail.instance_id}` ? 'Sending…' : 'Email to the client'}
                                    </button>
                                    <button onClick={() => deliver(detail.instance_id, 'in_person')}
                                            disabled={busy === `deliver-${detail.instance_id}`}
                                            className="px-3 py-1.5 text-sm rounded border hover:bg-gray-50 disabled:opacity-50">
                                        Given in person
                                    </button>
                                </>
                            ) : (
                                <button onClick={() => send(detail.instance_id)}
                                        disabled={busy === `send-${detail.instance_id}` || detail.status === 'signed'}
                                        className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                                    {busy === `send-${detail.instance_id}` ? 'Sending…' : 'Email for signature'}
                                </button>
                            )}
                            <button onClick={() => signInternal(detail.instance_id)}
                                    className="px-3 py-1.5 text-sm rounded border hover:bg-gray-50">
                                Sign for the firm
                            </button>
                            <label className="px-3 py-1.5 text-sm rounded border hover:bg-gray-50 cursor-pointer">
                                Upload signed scan
                                <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg"
                                       onChange={(e) => e.target.files[0] && uploadScan(detail.instance_id, e.target.files[0])} />
                            </label>
                            <button onClick={() => voidForm(detail.instance_id)}
                                    className="px-3 py-1.5 text-sm rounded border border-red-200 text-red-700 hover:bg-red-50">
                                Void
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default FormsPanel;
